/**
 * Клиенты: что о человеке видно в админке. Регистрация идёт по номеру
 * телефона в приложении, поэтому номер здесь — ещё и идентификатор.
 *
 * История баллов приходит готовой лентой (`GET /admin/customers/{id}` →
 * `ledger`): начисления при оплате, списания в заказе и возвраты считает
 * сервер (R-10…R-14) — собирать её из заказов больше не нужно.
 */
import { formatDate, parseIsoDay, withCountRu } from '@dorixona/utils'
import type { OrderStatus } from '../orders/api'
import type { Customer, CustomerLang, LedgerEntry, LedgerType } from './api'

/** Из чего движение баллов: подпись строки в истории. */
export const LEDGER_TEXT: Record<LedgerType, string> = {
  welcome_bonus: 'Приветственный бонус за регистрацию',
  birthday_bonus: 'Подарок ко дню рождения',
  order_accrual: 'Кэшбэк за заказ',
  offline_accrual: 'Начисление за покупку в аптеке',
  offline_spend: 'Списание при покупке в аптеке',
  order_spend: 'Оплата баллами',
  spend_release: 'Возврат баллов',
}

export const ledgerText = (type: LedgerType): string => LEDGER_TEXT[type] ?? type

/**
 * Основание движения — вторая строка под названием: когда и за что баллы
 * пришли или ушли. Номера заказа в ленте нет (только `order_id`), поэтому
 * сам заказ открывается ссылкой в строке.
 */
export const LEDGER_NOTE: Record<LedgerType, string> = {
  welcome_bonus: 'начислен при регистрации в программе',
  birthday_bonus: 'начислен ко дню рождения',
  order_accrual: 'начислен при оплате заказа',
  offline_accrual: 'кэшбэк за покупку на кассе аптеки',
  offline_spend: 'оплата баллами на кассе аптеки',
  order_spend: 'списано в счёт оплаты заказа',
  spend_release: 'заказ не состоялся — баллы вернулись',
}

export const ledgerNote = (type: LedgerType): string => LEDGER_NOTE[type] ?? ''

/** Сколько движений отдаёт карточка клиента: последние 50, без страниц. */
export const LEDGER_LIMIT = 50

/**
 * Число у вкладки «Транзакции». Меньше 50 — это вся история; ровно 50 —
 * сервер обрезал ленту, и настоящего итога мы не знаем: «50+», а не «50».
 */
export const ledgerCount = (length: number): string =>
  length < LEDGER_LIMIT ? String(length) : `${LEDGER_LIMIT}+`

/** Завершённые заказы: деньги получены, товар у клиента. */
export const DONE_STATUSES: OrderStatus[] = ['delivered', 'issued']

/**
 * Не состоялись: отменять заказ в системе некому, он просто истекает —
 * не оплатили вовремя или не забрали бронь (R-39).
 */
export const LOST_STATUSES: OrderStatus[] = ['payment_expired', 'reservation_expired']

/**
 * Полных лет на дату `today`; обе даты — `YYYY-MM-DD`. Считаем по строкам,
 * без часов: день рождения — календарная дата, а не момент времени.
 * Непонятная дата или дата из будущего — null.
 */
export function ageYears(birth: string, today: string): number | null {
  if (parseIsoDay(birth) === null || parseIsoDay(today) === null) return null
  const years =
    Number(today.slice(0, 4)) -
    Number(birth.slice(0, 4)) -
    (today.slice(5) < birth.slice(5) ? 1 : 0)
  return years >= 0 ? years : null
}

/** «01.05.1990 · 36 лет»; не указана — так и пишем. */
export function birthText(birth: string, today: string): string {
  const day = parseIsoDay(birth)
  if (day === null) return 'не указана'
  const age = ageYears(birth, today)
  const date = formatDate(day)
  return age === null ? date : `${date} · ${withCountRu(age, ['год', 'года', 'лет'])}`
}

/** Откуда клиент пришёл: привязанный Telegram означает регистрацию через бота. */
export const sourceText = (telegram: boolean): string => (telegram ? 'Telegram-бот' : 'приложение')

/** Пол одним словом; не указан — прочерк. */
export function genderText(gender: 'male' | 'female' | ''): string {
  if (gender === 'male') return 'М'
  if (gender === 'female') return 'Ж'
  return '—'
}

/** Язык приложения клиента. */
const LANG_TEXT: Record<CustomerLang, string> = {
  ru: 'русский',
  uz: 'узбекский',
  en: 'английский',
}

export const langText = (lang: CustomerLang): string => LANG_TEXT[lang]

/** Уведомления клиента: только push — СМС приходит лишь код входа (R-116). */
export function notifyText(push: boolean): string {
  return push ? 'push' : 'выключены'
}

export type CustomerStatus = '' | 'active' | 'blocked' | 'deleted'

type Deletion = Pick<Customer, 'deletedAt' | 'anonymizedAt'>

/**
 * Удалён ли клиент: аккаунт удалён (R-114) или данные стёрты (В-22). Такой
 * клиент — только для просмотра: правку и блокировку сервер отклонит (404).
 */
export const isDeleted = (customer: Deletion): boolean =>
  customer.deletedAt > 0 || customer.anonymizedAt > 0

/** Бейдж строки реестра: «Удалён 01.10.2026». Только для удалённых. */
export const deletedText = (customer: Deletion): string =>
  `Удалён ${formatDate(customer.deletedAt || customer.anonymizedAt)}`

/**
 * Статус удалённого в карточке. Удалил сам — данные ещё видны; стёрты —
 * от клиента осталась обезличенная история. Только для удалённых.
 */
export const deletionText = (customer: Deletion): string =>
  customer.anonymizedAt
    ? `Данные стёрты ${formatDate(customer.anonymizedAt)}`
    : `Удалён клиентом ${formatDate(customer.deletedAt)}`

/** Имя для экрана: у стёртого клиента его может не остаться — тогда номер. */
export const displayName = (customer: Pick<Customer, 'id' | 'name'>): string =>
  customer.name || `Клиент №${customer.id}`

/** Сколько строк на странице можно выбрать; больше 100 сервер не отдаёт. */
export const PAGE_SIZES = [10, 25, 50, 100] as const
export type PageSize = (typeof PAGE_SIZES)[number]

export interface CustomerFilters {
  q: string
  status: CustomerStatus
  /** Страница с 1; листает сервер. */
  page: number
  size: PageSize
}

export const EMPTY_FILTERS: CustomerFilters = { q: '', status: '', page: 1, size: 10 }

const STATUSES: CustomerStatus[] = ['', 'active', 'blocked', 'deleted']

function positive(raw: string | null, fallback: number): number {
  const n = Number(raw)
  return Number.isInteger(n) && n > 0 ? n : fallback
}

/**
 * Состояние реестра живёт в адресе: «Назад» из карточки клиента возвращает
 * на ту же страницу с тем же поиском. Мусор в адресе становится значением по
 * умолчанию, а не ломает экран.
 */
export function filtersFromParams(params: URLSearchParams): CustomerFilters {
  const status = params.get('status') as CustomerStatus
  const size = positive(params.get('size'), EMPTY_FILTERS.size) as PageSize
  return {
    q: params.get('q') ?? '',
    status: STATUSES.includes(status) ? status : EMPTY_FILTERS.status,
    page: positive(params.get('page'), 1),
    size: PAGE_SIZES.includes(size) ? size : EMPTY_FILTERS.size,
  }
}

/** Обратно в адрес — только то, что отличается от значения по умолчанию. */
export function filtersToParams(filters: CustomerFilters): URLSearchParams {
  const params = new URLSearchParams()
  for (const key of Object.keys(EMPTY_FILTERS) as (keyof CustomerFilters)[]) {
    if (filters[key] !== EMPTY_FILTERS[key]) params.set(key, String(filters[key]))
  }
  return params
}

/** Сужен ли список: страница и её размер — не фильтры. */
export const isFiltered = (filters: CustomerFilters): boolean =>
  filters.q !== EMPTY_FILTERS.q || filters.status !== EMPTY_FILTERS.status

/** Сколько баллов пришло и сколько ушло по ленте движений. */
export interface LedgerTotals {
  earned: number
  spent: number
  /** Лента обрезана сервером (50 движений) — итог не за всё время. */
  partial: boolean
}

/**
 * Начислено — всё, что пришло, кроме возврата списанного; списано — всё, что
 * ушло на заказы, за вычетом возвращённого (заказ не состоялся — баллы
 * вернулись, R-33). Итог честный, пока лента не обрезана: на 50 движениях
 * сервер её обрезает, и это видно в подписи.
 */
export function ledgerTotals(ledger: LedgerEntry[]): LedgerTotals {
  let earned = 0
  let spent = 0
  for (const entry of ledger) {
    if (entry.type === 'spend_release') spent -= entry.amount
    else if (entry.amount > 0) earned += entry.amount
    else spent -= entry.amount
  }
  return { earned, spent: Math.max(0, spent), partial: ledger.length >= LEDGER_LIMIT }
}
